// Empire Apps Chassis · connectors/financial/bharat-bill-pay
// BBPS recurring biller list. Stub mode only — no public consumer API for
// reading a user's BBPS history. v1.1 wires NPCI BBPS BillerCore once we
// register as an OU (Operating Unit).

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type RecurringBill,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';

const STATUS_KEY = 'empire:financial:bharat-bill-pay:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly lastSync: string | null;
  readonly billerCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, lastSync: null, billerCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

function mockBills(): readonly RecurringBill[] {
  return [
    { billerId: 'bbps_bses_001', biller: 'BSES Yamuna Power', category: 'electricity', avgPaise: paise(385000), nextDueDate: '2026-06-12', autopay: true },
    { billerId: 'bbps_airtel_001', biller: 'Airtel Postpaid', category: 'mobile', avgPaise: paise(99900), nextDueDate: '2026-06-04', autopay: true },
    { billerId: 'bbps_jio_fiber_001', biller: 'Jio Fiber', category: 'broadband', avgPaise: paise(99900), nextDueDate: '2026-06-08', autopay: true },
    { billerId: 'bbps_tata_play_001', biller: 'Tata Play HD', category: 'dth', avgPaise: paise(40000), nextDueDate: '2026-06-15', autopay: false },
    { billerId: 'bbps_igl_001', biller: 'IGL Piped Gas', category: 'gas', avgPaise: paise(85000), nextDueDate: '2026-06-20', autopay: true },
    { billerId: 'bbps_djb_001', biller: 'Delhi Jal Board', category: 'water', avgPaise: paise(120000), nextDueDate: '2026-07-01', autopay: false },
    { billerId: 'bbps_lic_001', biller: 'LIC Premium', category: 'insurance', avgPaise: paise(2400000), nextDueDate: '2027-01-15', autopay: false },
  ];
}

export const bharatBillPay: Connector = {
  id: 'bharat-bill-pay',
  displayName: 'Bharat BillPay biller list',
  category: 'bill-pay',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.billerCount,
      disabledReason: 'BBPS BillerCore API requires NPCI OU registration. Stub mode only for v0.',
    };
  },

  async connect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-bbps-read', 'BBPS biller list (stub)');
    const connectedAt = isoTs();
    writeStatus({ connected: true, lastSync: connectedAt, billerCount: 0 });
    return ok({ connectedAt });
  },

  async import(): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    const bills = mockBills();
    writeStatus({ ...s, lastSync: isoTs(), billerCount: bills.length });
    return ok({ bills }, 'Mock-mode. BBPS BillerCore wiring deferred to v1.1.');
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, lastSync: null, billerCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-bbps-read' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { bills: mockBills() };
  },
};
